import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { ControlGroup } from './ControlGroup';
import { EditorSection } from './EditorSection';

export type LegendPositionPreset =
  | "top-center"
  | "right-outside"
  | "inside-top-right"
  | "inside-bottom-right";

export interface LegendEditorState {
  showLegend: boolean;
  orientation: "h" | "v";
  preset: LegendPositionPreset;
  offsetX: number; // -0.2 to +0.2
  offsetY: number; // -0.2 to +0.2
  fontSize: number; // 8 to 16
}

const LEGEND_PRESET_BASE: Record<LegendPositionPreset, {
  x: number;
  y: number;
  xanchor: "left" | "center" | "right";
  yanchor: "top" | "middle" | "bottom";
}> = {
  "top-center": {
    x: 0.5,
    y: 1.08,
    xanchor: "center",
    yanchor: "bottom",
  },
  "right-outside": {
    x: 1.02,
    y: 1.0,
    xanchor: "left",
    yanchor: "top",
  },
  "inside-top-right": {
    x: 0.99,
    y: 0.99,
    xanchor: "right",
    yanchor: "top",
  },
  "inside-bottom-right": {
    x: 0.99,
    y: 0.01,
    xanchor: "right",
    yanchor: "bottom",
  },
};

/**
 * Derives LegendEditorState from a Plotly layout object.
 * Infers the best-matching preset by finding the nearest preset in (x,y) space.
 */
export function deriveLegendStateFromLayout(layout: any): LegendEditorState {
  const legend = layout?.legend || {};
  const showLegend = layout?.showlegend !== false;
  const orientation = legend.orientation === "v" ? "v" : "h";
  const fontSize = legend?.font?.size ?? 10;

  // Get current x, y coordinates (default to top-center if missing)
  const currentX = legend.x ?? 0.5;
  const currentY = legend.y ?? 1.08;
  const currentXanchor = legend.xanchor || "center";
  const currentYanchor = legend.yanchor || "bottom";

  // Find the best-matching preset by computing distance to each preset's base position
  let bestPreset: LegendPositionPreset = "top-center";
  let minDistance = Infinity;

  for (const [preset, base] of Object.entries(LEGEND_PRESET_BASE)) {
    // Compute Euclidean distance, but also consider anchor matching
    const dx = currentX - base.x;
    const dy = currentY - base.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Prefer presets with matching anchors
    const anchorMatch = 
      (currentXanchor === base.xanchor ? 0.1 : 0) +
      (currentYanchor === base.yanchor ? 0.1 : 0);
    
    const adjustedDistance = distance - anchorMatch;
    
    if (adjustedDistance < minDistance) {
      minDistance = adjustedDistance;
      bestPreset = preset as LegendPositionPreset;
    }
  }

  // Compute offsets relative to the best-matching preset
  const base = LEGEND_PRESET_BASE[bestPreset];
  const offsetX = currentX - base.x;
  const offsetY = currentY - base.y;

  return {
    showLegend,
    orientation,
    preset: bestPreset,
    offsetX: Math.max(-0.2, Math.min(0.2, offsetX)),
    offsetY: Math.max(-0.2, Math.min(0.2, offsetY)),
    fontSize: Math.max(8, Math.min(16, fontSize)),
  };
}

/**
 * Applies LegendEditorState to a Plotly layout object.
 * Returns a new layout object with legend configuration updated.
 */
export function applyLegendStateToLayout(layout: any, state: LegendEditorState): any {
  const next = { ...layout };
  
  // Always set showlegend
  next.showlegend = state.showLegend;

  // Get base coordinates for the selected preset
  const base = LEGEND_PRESET_BASE[state.preset];

  // Build legend configuration
  // When showLegend is false, keep layout.legend populated so settings are preserved
  next.legend = {
    ...(next.legend ?? {}),
    orientation: state.orientation,
    xref: "paper",
    yref: "paper",
    x: base.x + state.offsetX,
    y: base.y + state.offsetY,
    xanchor: base.xanchor,
    yanchor: base.yanchor,
    font: {
      ...(next.legend?.font ?? {}),
      size: state.fontSize,
    },
  };

  return next;
}

interface LegendControlsProps {
  plotlyJson?: any;
  onUpdate: (updateType: string, value: any) => void;
}

export function LegendControls({ plotlyJson, onUpdate }: LegendControlsProps) {
  const layout = plotlyJson?.layout || {};

  // Derive initial state from layout
  const [state, setState] = useState<LegendEditorState>(() => 
    deriveLegendStateFromLayout(layout)
  );

  // Update state when layout changes externally
  useEffect(() => {
    const newState = deriveLegendStateFromLayout(layout);
    setState(newState);
  }, [
    layout?.showlegend,
    layout?.legend?.orientation,
    layout?.legend?.x,
    layout?.legend?.y,
    layout?.legend?.xanchor,
    layout?.legend?.yanchor,
    layout?.legend?.font?.size,
  ]);

  // Apply state changes to layout
  const applyState = (newState: LegendEditorState) => {
    setState(newState);
    const updatedLayout = applyLegendStateToLayout(layout, newState);
    
    // Apply updates atomically using batch.apply to prevent stale state overwrites
    // Both showlegend and legend must update together, otherwise sequential calls
    // will clone the same stale currentPlot and the second update overwrites the first
    onUpdate('batch.apply', [
      { type: 'layout.showlegend', value: updatedLayout.showlegend },
      { type: 'layout.legend', value: updatedLayout.legend }
    ]);
  };

  const presetLabels: Record<LegendPositionPreset, string> = {
    "top-center": "Top center",
    "right-outside": "Right side",
    "inside-top-right": "Inside top-right",
    "inside-bottom-right": "Inside bottom-right",
  };

  return (
    <div className="space-y-3">
      {/* Header: Show legend toggle */}
      <div className="flex items-center justify-between">
        <Label htmlFor="show-legend" className="text-xs font-medium text-foreground">
          Legend
        </Label>
        <Switch
          id="show-legend"
          checked={state.showLegend}
          onCheckedChange={(checked) => {
            applyState({ ...state, showLegend: checked });
          }}
        />
      </div>

      {/* Legend controls (only shown when legend is visible) */}
      {state.showLegend && (
        <div className="space-y-3 pt-1">
          {/* Orientation */}
          <ControlGroup label="Orientation">
            <ToggleGroup
              type="single"
              value={state.orientation}
              onValueChange={(value) => {
                if (value === "h" || value === "v") {
                  applyState({ ...state, orientation: value });
                }
              }}
              className="w-full"
            >
              <ToggleGroupItem value="h" className="flex-1">
                Horizontal
              </ToggleGroupItem>
              <ToggleGroupItem value="v" className="flex-1">
                Vertical
              </ToggleGroupItem>
            </ToggleGroup>
          </ControlGroup>

          {/* Position presets */}
          <ControlGroup label="Position">
            <div className="grid grid-cols-2 gap-2">
              {(Object.keys(LEGEND_PRESET_BASE) as LegendPositionPreset[]).map((preset) => (
                <Button
                  key={preset}
                  variant={state.preset === preset ? "default" : "outline"}
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => {
                    // Reset offsets when changing preset
                    applyState({ ...state, preset, offsetX: 0, offsetY: 0 });
                  }}
                >
                  {presetLabels[preset]}
                </Button>
              ))}
            </div>
          </ControlGroup>

          {/* Advanced controls (collapsible) */}
          <EditorSection
            id="legend-advanced"
            title="Advanced"
            collapsible
            defaultCollapsed
          >
            <div className="space-y-3">
              {/* Offset X */}
              <ControlGroup label="Horizontal offset">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground">
                      {state.offsetX >= 0 ? '+' : ''}{state.offsetX.toFixed(2)}
                    </span>
                  </div>
                  <Slider
                    value={[state.offsetX]}
                    min={-0.2}
                    max={0.2}
                    step={0.01}
                    onValueChange={([value]) => {
                      applyState({ ...state, offsetX: value });
                    }}
                  />
                </div>
              </ControlGroup>

              {/* Offset Y */}
              <ControlGroup label="Vertical offset">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground">
                      {state.offsetY >= 0 ? '+' : ''}{state.offsetY.toFixed(2)}
                    </span>
                  </div>
                  <Slider
                    value={[state.offsetY]}
                    min={-0.2}
                    max={0.2}
                    step={0.01}
                    onValueChange={([value]) => {
                      applyState({ ...state, offsetY: value });
                    }}
                  />
                </div>
              </ControlGroup>

              {/* Font size */}
              <ControlGroup label="Legend text size">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground">
                      {state.fontSize}px
                    </span>
                  </div>
                  <Slider
                    value={[state.fontSize]}
                    min={8}
                    max={16}
                    step={1}
                    onValueChange={([value]) => {
                      applyState({ ...state, fontSize: value });
                    }}
                  />
                </div>
              </ControlGroup>
            </div>
          </EditorSection>
        </div>
      )}
    </div>
  );
}
